/**
 * 富芮坤默认 DPI 阶梯（与协议层空配置回退、resetDpi/factoryCfg、DPI 页「恢复默认」一致）
 */
import { getSupportedDevicesSync, dpiMax } from "@eweadn-qiankun/shared-utils";
import type { FreqLinkScheme } from "./deviceProtocol";

/** 400 起跳（6 档） */
const LADDER_400 = [400, 800, 1600, 3200, 6400, 12000] as const;

/** 800 起跳（6 档） */
const LADDER_800 = [800, 1600, 2400, 3200, 6400, 12000] as const;

/**
 * 机型默认档位。新机型在此登记
 * g2sea 为 5 档特例。
 */
const DEVICE_LADDERS: Record<string, readonly number[]> = {
  // 400 起跳
  s9se: LADDER_400,
  x23: LADDER_400,
  x23max: LADDER_400,
  x23se: LADDER_800,
  x23lcarbon: LADDER_400,
  a6pro: LADDER_400,
  a6max: LADDER_400,
  a7se: LADDER_400,
  a7: LADDER_400,
  a7pro: LADDER_400,
  s7: LADDER_400,
  s7plus: LADDER_400,
  s2: LADDER_400,
  s7sea: LADDER_400,
  s7se: LADDER_400,
  g5: LADDER_400,
  e7: LADDER_400,
  e7v3: LADDER_400,
  // 800 起跳
  x23carbon2: LADDER_800,
  x23carbon: LADDER_800,
  x23se2: LADDER_800,
  x23pro2: LADDER_800,
  x23pro3: LADDER_800,
  e7pro: LADDER_800,
  e9: LADDER_800,
  e9carbon: LADDER_800,
  e9procarbon: LADDER_800,
  e11: LADDER_800,
  e11pro: LADDER_800,
  a6: LADDER_800,
  g2sea: [800, 1600, 3200, 6400, 12800],
};

/** 档位数强制为阶梯长度（不再跟读帧 dpi_count 抬到 6） */
const FIXED_GEAR_COUNT_IDS = new Set(["g2sea"]);

/** 可设置上限取阶梯最大值（如 g2sea=12800），其余走传感器上限 */
const DPI_CAP_FROM_LADDER_IDS = new Set(["g2sea"]);

function normalizeDeviceKey(raw: string): string {
  return String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
}

function isFreqLinkScheme(v: unknown): v is FreqLinkScheme {
  return v === "a" || v === "c";
}

/** 从 localStorage / 入参解析型号 deviceId（如 s9se） */
export function resolveFreqDpiDeviceId(deviceId?: string): string {
  const tryResolve = (raw: string): string => {
    const key = normalizeDeviceKey(raw);
    if (!key) return "";
    if (DEVICE_LADDERS[key]) return key;
    try {
      const row = getSupportedDevicesSync().mouse.find(
        (d) =>
          normalizeDeviceKey(String(d.deviceId ?? "")) === key ||
          normalizeDeviceKey(String(d.displayname ?? "")) === key
      );
      const id = normalizeDeviceKey(String(row?.deviceId ?? ""));
      if (id) return id;
    } catch {
      /* ignore */
    }
    return key;
  };

  if (deviceId) {
    const hit = tryResolve(deviceId);
    if (hit) return hit;
  }
  if (typeof window === "undefined") return "";
  try {
    const saved = localStorage.getItem("currentDeviceId") || "";
    if (saved) {
      const hit = tryResolve(saved);
      if (hit) return hit;
    }
    const raw = localStorage.getItem("equipmentData");
    if (raw) {
      const id = JSON.parse(raw)?.deviceId;
      if (typeof id === "string" && id) return tryResolve(id);
    }
  } catch {
    /* ignore */
  }
  return "";
}

/** 设备表 / 入参 → A|C（仅协议方案，不决定 DPI 阶梯）；未声明则 a */
export function resolveFreqDpiScheme(
  deviceId?: string,
  scheme?: FreqLinkScheme
): FreqLinkScheme {
  if (isFreqLinkScheme(scheme)) return scheme;

  const id = resolveFreqDpiDeviceId(deviceId);
  try {
    const mouse = getSupportedDevicesSync().mouse as Array<{
      deviceId?: string;
      displayname?: string;
      solutionRovider?: string;
      freqLinkHint?: FreqLinkScheme;
    }>;
    const row = mouse.find(
      (d) =>
        d?.solutionRovider === "freq" &&
        (normalizeDeviceKey(String(d.deviceId ?? "")) === id ||
          normalizeDeviceKey(String(d.displayname ?? "")) === id)
    );
    // 命中设备表即定案，勿再回落 store（双鼠时 store 可能是另一只的 A/C）
    if (row) return row.freqLinkHint === "c" ? "c" : "a";
  } catch {
    /* ignore */
  }

  // 设备表未命中且无可靠 deviceId 时，才回落 store（避免双鼠串方案）
  if (!id && typeof window !== "undefined") {
    try {
      const w = window as Window & {
        mouseStoreApi?: {
          getState?: () => {
            deviceInfo?: { mouseCfg?: { freqLinkScheme?: FreqLinkScheme } };
          };
        };
      };
      const fromCfg =
        w.mouseStoreApi?.getState?.()?.deviceInfo?.mouseCfg?.freqLinkScheme;
      if (isFreqLinkScheme(fromCfg)) return fromCfg;
    } catch {
      /* ignore */
    }
  }

  return "a";
}

function resolveLadder(deviceId?: string): readonly number[] {
  const id = resolveFreqDpiDeviceId(deviceId);
  if (id && DEVICE_LADDERS[id]) return DEVICE_LADDERS[id];
  return LADDER_400;
}

/** 该机型档位数；有机型表时用表长度。scheme 仅保留调用兼容，不参与档位 */
export function getFreqDpiGearCount(
  deviceId?: string,
  _scheme?: FreqLinkScheme
): number {
  void _scheme;
  return resolveLadder(deviceId).length;
}

/**
 * 是否有机型固定档位数（与「读帧 dpi_count 再 Math.max(6,…)」相对）。
 * 有固定档时 UI / store 一律用固定值，不再抬到 6。
 */
export function hasFixedFreqDpiGearCount(deviceId?: string): boolean {
  const id = resolveFreqDpiDeviceId(deviceId);
  return Boolean(id && FIXED_GEAR_COUNT_IDS.has(id));
}

/** 读帧档位数 → UI/store 档位数（固定机型强制覆盖） */
export function resolveFreqDpiStageCount(
  reported: number,
  deviceId?: string,
  scheme?: FreqLinkScheme
): number {
  if (hasFixedFreqDpiGearCount(deviceId)) {
    return getFreqDpiGearCount(deviceId, scheme);
  }
  return Math.max(1, Math.min(8, Number(reported) || 6));
}

export function clampFreqDpiToCap(v: number, cap: number): number {
  const n = Number(v) || 400;
  const stepped = Math.ceil(n / 50) * 50;
  return Math.max(50, Math.min(stepped, cap));
}

/**
 * 机型阶梯封顶的 DPI 上限（取阶梯最大值，如 g2sea=12800）。
 * 未声明时返回 undefined，由调用方走传感器上限。
 */
export function getFreqDeviceDpiCap(deviceId?: string): number | undefined {
  const id = resolveFreqDpiDeviceId(deviceId);
  if (!id || !DPI_CAP_FROM_LADDER_IDS.has(id)) return undefined;
  const ladder = DEVICE_LADDERS[id];
  if (!ladder?.length) return undefined;
  return Math.max(...ladder);
}

/**
 * 富芮坤可设置的 DPI 上限：优先机型阶梯封顶，其次传感器（含 3395=26000）。
 */
export function resolveFreqDpiCap(deviceId?: string): number {
  const deviceCap = getFreqDeviceDpiCap(deviceId);
  if (deviceCap != null) return deviceCap;

  let sensor = "3950";
  try {
    const id = resolveFreqDpiDeviceId(deviceId);
    const mouseDevice = getSupportedDevicesSync().mouse.find(
      (d) =>
        String(d.deviceId ?? "").toLowerCase() === id ||
        String(d.displayname ?? "").toLowerCase() === id ||
        (deviceId &&
          (String(d.deviceId ?? "") === deviceId ||
            String(d.displayname ?? "") === deviceId))
    );
    if (mouseDevice?.sensor) sensor = String(mouseDevice.sensor);
  } catch {
    /* ignore */
  }
  if (String(sensor).trim() === "3395") return 26000;
  return dpiMax(sensor);
}

/**
 * 按传感器上限封顶并 50 步进对齐后的默认档位表。
 * 按型号查表；未登记机型回落 400 起跳。scheme 仅保留调用兼容。
 */
export function getFreqDefaultDpiPreset(
  cap: number,
  deviceId?: string,
  _scheme?: FreqLinkScheme
): number[] {
  void _scheme;
  return resolveLadder(deviceId).map((x) => clampFreqDpiToCap(x, cap));
}
